import React, { useState } from 'react'
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  PhoneIcon,
  ComputerDesktopIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import {
  useGetGuestToursQuery,
  useGetGuestTourStatsQuery,
  useUpdateGuestTourStatusMutation,
  useUpdateGuestTourNotesMutation,
  useDeleteGuestTourMutation,
  GuestTourFilters
} from '../store/api/guestToursApi'
import SortableTableHeader from '../components/SortableTableHeader'
import { formatDateTimeEST } from '../utils/dateUtils'

const GuestTours: React.FC = () => {
  const [filters, setFilters] = useState<GuestTourFilters>({
    page: 1,
    limit: 20,
    search: '',
    status: 'all',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const [editingNotes, setEditingNotes] = useState<number | null>(null);
  const [notesValue, setNotesValue] = useState('');

  const { data: toursData, isLoading, error } = useGetGuestToursQuery(filters)
  const { data: statsData, isLoading: statsLoading } = useGetGuestTourStatsQuery()
  const [updateStatus] = useUpdateGuestTourStatusMutation()
  const [updateNotes] = useUpdateGuestTourNotesMutation()
  const [deleteTour] = useDeleteGuestTourMutation()

  // Handle search
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof GuestTourFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle sorting
  const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle copy invitation link
  const handleCopyInvitationLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Invitation link copied to clipboard!');
  };

  // Handle status change
  const handleStatusChange = async (id: number, status: string) => {
    try {
      await updateStatus({ id, status }).unwrap();
      toast.success('Status updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update status');
    }
  };

  // Handle notes edit
  const startEditingNotes = (id: number, currentNotes: string | null) => {
    setEditingNotes(id);
    setNotesValue(currentNotes || '');
  };

  const cancelEditingNotes = () => {
    setEditingNotes(null);
    setNotesValue('');
  };

  const saveNotes = async (id: number) => {
    try {
      await updateNotes({ id, notes: notesValue }).unwrap();
      setEditingNotes(null);
      setNotesValue('');
      toast.success('Notes updated successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update notes');
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await deleteTour(id).unwrap();
      toast.success('Application deleted successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete application');
    }
  };

  const applications = toursData?.data || []
  const stats = statsData?.data || {
    totalApplications: 0,
    newApplications: 0,
    contactedApplications: 0,
    demoSentApplications: 0,
    completedApplications: 0,
    wantsCallCount: 0,
    wantsGuestAccessCount: 0
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading guest tour applications. Please try again.</p>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800'
      case 'contacted': return 'bg-yellow-100 text-yellow-800'
      case 'demo_sent': return 'bg-purple-100 text-purple-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'declined': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Guest Tours</h1>
          <p className="text-gray-600">View and manage pilot application submissions</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">New</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.newApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-yellow-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Contacted</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.contactedApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-purple-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Demo Sent</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.demoSentApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-green-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.completedApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PhoneIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Wants Call</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.wantsCallCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ComputerDesktopIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Wants Demo</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.wantsGuestAccessCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search applications..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="demo_sent">Demo Sent</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
            </select>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTableHeader
                  label="Name"
                  sortKey="name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Email"
                  sortKey="email"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Company"
                  sortKey="company"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interests
                </th>
                <SortableTableHeader
                  label="Status"
                  sortKey="status"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Created"
                  sortKey="created_at"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {applications.map((app) => (
                <React.Fragment key={app.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{app.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{app.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{app.company}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {app.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {app.team_size || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {app.wants_call === 1 && (
                          <PhoneIcon className="h-4 w-4 text-green-600" title="Wants call" />
                        )}
                        {app.wants_guest_access === 1 && (
                          <ComputerDesktopIcon className="h-4 w-4 text-purple-600" title="Wants demo access" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={app.status}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className={`text-xs font-semibold rounded-full px-2 py-1 ${getStatusColor(app.status)}`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="demo_sent">Demo Sent</option>
                        <option value="completed">Completed</option>
                        <option value="declined">Declined</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTimeEST(app.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        {app.invitation_url && (
                          <button
                            onClick={() => handleCopyInvitationLink(app.invitation_url!)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Copy invitation link"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => startEditingNotes(app.id, app.notes)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Edit notes"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(app.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={9} className="px-6 py-2 bg-gray-50">
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs font-semibold text-gray-600">Project Description:</span>
                          <p className="text-sm text-gray-900 mt-1">{app.project_description}</p>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600">Notes:</span>
                            {editingNotes === app.id && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => saveNotes(app.id)}
                                  className="text-green-600 hover:text-green-800"
                                  title="Save"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={cancelEditingNotes}
                                  className="text-red-600 hover:text-red-800"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          {editingNotes === app.id ? (
                            <textarea
                              value={notesValue}
                              onChange={(e) => setNotesValue(e.target.value)}
                              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={3}
                              placeholder="Add notes..."
                            />
                          ) : (
                            <p className="text-sm text-gray-900 mt-1">{app.notes || 'No notes'}</p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {toursData?.meta && toursData.meta.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, (toursData.meta?.page || 1) - 1))}
                disabled={toursData.meta.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(toursData.meta.totalPages, (toursData.meta?.page || 1) + 1))}
                disabled={toursData.meta.page === toursData.meta.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((toursData.meta.page - 1) * toursData.meta.limit) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(toursData.meta.page * toursData.meta.limit, toursData.meta.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{toursData.meta.total}</span>{' '}
                  applications
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(Math.max(1, toursData.meta.page - 1))}
                    disabled={toursData.meta.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, toursData.meta.totalPages))].map((_, index) => {
                    const pageStart = Math.max(1, Math.min(toursData.meta.totalPages - 4, toursData.meta.page - 2));
                    const pageNum = pageStart + index;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === toursData.meta.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(Math.min(toursData.meta.totalPages, toursData.meta.page + 1))}
                    disabled={toursData.meta.page === toursData.meta.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GuestTours

